import React, { FormEvent, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectDynSess,
  selectIsFurniture,
  selectLlmAPIVersion,
} from '@next-app/lib/features/InitSelectors';
import LLButton from '@next-app/components/elements/LLButton/LLButton';
import {
  applyPromoCode,
  handleCheckoutServerErrors,
  triggerCheckoutFormErrorEvent,
} from '@next-app/utils/CheckoutHelper';
import { isNotEmptyObject } from '@next-app/utils/common-utils';
import Conditional from '@next-app/components/Conditional';
import { setLoadCheckoutDetails } from '@next-app/lib/features/checkout/CheckoutSlice';
import {
  CheckoutServerErrorResponse,
  ShippingAddressResponse,
} from '@next-app/lib/features/checkout/Checkout.interface';
import {
  CHECKOUT_VALIDATION_MESSAGES,
  FIELD_INVALID_CHARACTERS,
} from '@next-app/constants/CheckoutConstants';
import { ShoppingCartData } from '@next-app/lib/features/ShoppingCart/ShoppingCart.interface';
import { setShoppingCartData } from '@next-app/lib/features/ShoppingCart/ShoppingCartSlice';
import { DynamicDTMEvent } from '@next-app/utils/dtm-event';
import { dyEvent } from '@next-app/utils/dy-event';
import Loader from '@next-app/components/shared/Loader/Loader';

interface FormException {
  propertyPath: string;
  localizedMessage: string;
  errorCode: string;
}

interface ChildProps {
  setFieldError: (value: boolean) => void;
  fieldError: boolean;
  pageType: 'checkout' | 'shopping-cart';
  appliedCouponCode?: string;
}

const ApplyPromoCodeForm: React.FC<ChildProps> = ({
  setFieldError,
  fieldError,
  pageType,
  appliedCouponCode = '',
}) => {
  /***
   * UseState
   */
  const [inputVal, setInputVal] = useState(appliedCouponCode);
  const [formErrors, setFormErrors] = useState<Record<string, string>>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (appliedCouponCode) {
      setInputVal(appliedCouponCode);
    }
  }, [appliedCouponCode]);

  /**
   *  dispatch
   */
  const dispatch = useDispatch();

  /**
   * Use Selector
   */
  const dynSessConf = useSelector(selectDynSess);
  const llmAPIVersion = useSelector(selectLlmAPIVersion);
  const isFurniture = useSelector(selectIsFurniture);

  /**
   * Method
   */
  const promoInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    value = value.replace(FIELD_INVALID_CHARACTERS.PROMO_CODE, '');
    setInputVal(value);
  };

  // Adding propertyPath to the error object
  const errorMessageHandler = (error: FormException[]) => {
    if (!Array.isArray(error)) {
      return [];
    }

    return error.map((item) => ({
      ...item,
      propertyPath: item?.propertyPath ?? `promoCode`,
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const isFormValid = () => !!inputVal.trim();
    if (isFormValid()) {
      try {
        setLoading(true);
        const applyPromoCodeResponse: (
          | ShippingAddressResponse
          | ShoppingCartData
        ) &
          CheckoutServerErrorResponse = await applyPromoCode(
          {
            pageName: pageType,
            couponClaimCode: inputVal,
            _dynSessConf: dynSessConf,
            howMany: -1,
            start: 0,
          },
          isFurniture,
          dispatch,
        );
        if (isNotEmptyObject(applyPromoCodeResponse)) {
          if (applyPromoCodeResponse?.formError) {
            triggerCheckoutFormErrorEvent({
              step: 'applyPromoCode',
              llmAPIVersion,
              isFurniture,
              errors: applyPromoCodeResponse?.formExceptions,
              fieldName: 'promoCode',
            });
            handleCheckoutServerErrors(
              errorMessageHandler(applyPromoCodeResponse?.formExceptions),
              setFormErrors,
            );
            setInputVal('');
            setFieldError(true);
          } else if (
            pageType === 'checkout' &&
            'checkoutResponse' in applyPromoCodeResponse
          ) {
            if (
              applyPromoCodeResponse?.checkoutResponse?.cartSummary
                ?.qualifierMessages &&
              applyPromoCodeResponse?.checkoutResponse?.cartSummary
                ?.qualifierMessages?.length > 0
            ) {
              triggerCheckoutFormErrorEvent({
                step: 'applyPromoCode',
                llmAPIVersion,
                isFurniture,
                errors: [
                  {
                    propertyPath: 'promoCode',
                    errorCode: 'promoCode',
                    localizedMessage:
                      applyPromoCodeResponse?.checkoutResponse?.cartSummary
                        ?.qualifierMessages[0],
                  },
                ],
              });
            } else {
              const eventData = {
                event: 'applyCoupon',
                detail: {
                  couponCode: inputVal,
                  llmAPIVersion: llmAPIVersion,
                  fromPage: 'checkoutPage',
                  isFurniture: isFurniture,
                },
              };
              DynamicDTMEvent(eventData);
            }
            dyEvent({
              eventName: 'Promo Code Entered',
              properties: {
                dyType: 'enter-promo-code-v1',
                code: inputVal,
              },
            });
            dispatch(
              setLoadCheckoutDetails(applyPromoCodeResponse?.checkoutResponse),
            );
            setInputVal('');
            setFieldError(false);
          } else if (
            pageType === 'shopping-cart' &&
            'shoppingCartResponse' in applyPromoCodeResponse
          ) {
            const cartSummary =
              applyPromoCodeResponse?.shoppingCartResponse &&
              typeof applyPromoCodeResponse.shoppingCartResponse === 'object' &&
              applyPromoCodeResponse.shoppingCartResponse !== null &&
              'cartSummary' in applyPromoCodeResponse.shoppingCartResponse
                ? (applyPromoCodeResponse.shoppingCartResponse.cartSummary as {
                    qualifierMessages?: string[];
                  })
                : undefined;

            if (
              cartSummary?.qualifierMessages &&
              cartSummary.qualifierMessages.length > 0
            ) {
              triggerCheckoutFormErrorEvent({
                step: 'applyPromoCode',
                llmAPIVersion,
                isFurniture,
                errors: [
                  {
                    propertyPath: 'promoCode',
                    errorCode: 'promoCode',
                    localizedMessage: cartSummary.qualifierMessages[0],
                  },
                ],
              });
            } else {
              const eventData = {
                event: 'applyCoupon',
                detail: {
                  couponCode: inputVal,
                  llmAPIVersion: llmAPIVersion,
                  fromPage: 'shopping-cart',
                  isFurniture: isFurniture,
                },
              };
              DynamicDTMEvent(eventData);
            }
            dyEvent({
              eventName: 'Promo Code Entered',
              properties: {
                dyType: 'enter-promo-code-v1',
                code: inputVal,
              },
            });
            dispatch(
              setShoppingCartData(applyPromoCodeResponse?.shoppingCartResponse),
            );
          }
        }
      } catch (err) {
        // Optional: log error or handle it
        console.error('Apply promo code failed:', err);
      } finally {
        setLoading(false); // Always stop loader
      }
    } else {
      setFormErrors({
        promoCode: CHECKOUT_VALIDATION_MESSAGES.PROMO_CODE_REQUIRED,
      });
      setFieldError(true);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className="row g-ll-3 align-items-start">
          <div className="col-8 mt-ll-3">
            <input
              type="text"
              className={`form-control p-ll-10 fs-ll-sm-16 fs-ll-lg-13 tc-717171 placeholder-717171-color ${formErrors?.promoCode && fieldError ? 'border-danger' : ''}`}
              style={{
                letterSpacing: '-0.2px',
                height: '44px',
                fontWeight: '100',
                borderRadius: isFurniture ? '0px' : '5px',
              }}
              placeholder="Promotional Code"
              value={inputVal}
              onChange={promoInputHandler}
              maxLength={40}
              automation-id="sc_coupon_input"
            />
            <Conditional if={formErrors !== undefined && fieldError}>
              <p
                className="invalid-feedback d-block lh-sm fw-light mt-ll-5-px fs-xs"
                style={{ letterSpacing: '-0.18px' }}
              >
                {formErrors?.promoCode}
              </p>
            </Conditional>
          </div>
          <div style={{ height: '44px' }} className="col-4 mt-ll-3">
            <LLButton
              type="submit"
              variation="primary"
              customClass={
                'w-100 h-100 fw-bold fs-base py-2 d-flex justify-content-center align-items-center'
              }
              automationId="sc_apply_coupon"
            >
              Apply
            </LLButton>
          </div>
        </div>
      </form>
      <Conditional if={loading}>
        <Loader aria-label="Promo code apply" />
      </Conditional>
    </>
  );
};

export default ApplyPromoCodeForm;
